import { useMemo, useRef } from "react"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { DrilldownVirtualizedGrid } from "./DrilldownVirtualizedGrid"
import { DrilldownCompetitorCreativeCard } from "./DrilldownCompetitorCreativeCard"
import { mapCompetitorCardRow } from "../lib/drilldown-competitor-card-mapper"
import { useCreativeUrlSettings } from "@/lib/queries/system-settings.queries"
import type { CompetitorCreativeCardModel } from "../lib/drilldown-card.types"
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll"

interface DrilldownGridViewProps {
  /** Accumulated, already globally-sorted rows from every loaded page (do NOT re-sort). */
  rows: Array<Record<string, unknown>>
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
  /** Total rows loaded from the server (unfiltered). Used as the pagination recheck key so client-side search filtering doesn't trigger spurious fetches. */
  loadedCount: number
  onOpenDrawer?: (id: string) => void
}

export function DrilldownGridView({
  rows,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  loadedCount,
  onOpenDrawer,
}: DrilldownGridViewProps) {
  const { t } = useTranslation()
  const { competitorsPath } = useCreativeUrlSettings()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Backend returns one globally-sorted stream paginated by offset, so pages
  // append in order — map straight through, never re-sort (that would make
  // later pages jump above earlier ones).
  const items = useMemo<CompetitorCreativeCardModel[]>(
    () => rows.map((row, index) => mapCompetitorCardRow(row, index, competitorsPath)),
    [rows, competitorsPath],
  )

  useInfiniteScroll(
    sentinelRef,
    scrollContainerRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    { recheckKey: loadedCount },
  )

  return (
    <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-auto" data-drilldown-scroll>
      <DrilldownVirtualizedGrid
        items={items}
        emptyLabel={t("drilldown.noCreativesMatch", "No creatives match the applied filters.")}
        scrollContainerRef={scrollContainerRef}
        getKey={(item) => item.id}
        renderItem={(item) => (
          <DrilldownCompetitorCreativeCard
            creative={item}
            onOpenDrawer={onOpenDrawer ? () => onOpenDrawer(item.id) : undefined}
          />
        )}
      />
      {hasNextPage && (
        <div ref={sentinelRef} className="flex items-center justify-center py-6">
          {isFetchingNextPage && (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          )}
        </div>
      )}
    </div>
  )
}
